import React, { useEffect, useState } from 'react';
import StockService from '../Api_Module/Api_Services/StockService';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

// Updated mover types per request
type MoverType = 'gainers' | 'losers' | 'active' | 'industry' | 'sector';

interface MoverItem {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  exchange: string;
}

interface PeSnapshotItem {
  date: string;
  pe: number;
  name: string; // Normalized from 'sector' or 'industry'
  exchange: string;
}

interface ApiMoverItem {
  symbol?: string;
  name?: string;
  price?: number | string;
  change?: number | string;
  changePercent?: number | string;
  changesPercentage?: number | string;
  exchange?: string;
}

interface ApiPeSnapshotItem {
  date: string;
  pe: number;
  sector?: string;
  industry?: string;
  exchange: string;
}

function normalizeApiItem(item: ApiMoverItem): MoverItem | null {
  const symbol = item.symbol?.trim();
  if (!symbol) return null;

  const price = parseFloat(String(item.price)) || 0;
  const change = parseFloat(String(item.change)) || 0;
  const changePercentRaw = item.changesPercentage ?? item.changePercent ?? 0;
  const changePercent = parseFloat(String(changePercentRaw)) || 0;
  const exchange = item.exchange?.trim() || '';

  return {
    symbol,
    name: item.name || symbol,
    price,
    change,
    changePercent,
    exchange,
  };
}

function normalizePeSnapshotItem(item: ApiPeSnapshotItem, type: 'sector' | 'industry'): PeSnapshotItem | null {
  const name = type === 'sector' ? item.sector : item.industry;
  if (!name) return null;

  return {
    date: item.date,
    pe: item.pe,
    name,
    exchange: item.exchange,
  };
}

// Tabs now include Industry and Sector
const moverTypeTabs: { id: MoverType; label: string }[] = [
  { id: 'gainers', label: 'Gainers' },
  { id: 'losers', label: 'Losers' },
  { id: 'active', label: 'Active' },
  { id: 'industry', label: 'Industry' },
  { id: 'sector', label: 'Sector' },
];

const getPreviousWeekday = () => {
  const today = new Date();
  const day = today.getDay();
  let daysToSubtract = 1;
  if (day === 0) { // Sunday
    daysToSubtract = 2;
  } else if (day === 1) { // Monday
    daysToSubtract = 3;
  }
  const previousDay = new Date(today);
  previousDay.setDate(today.getDate() - daysToSubtract);
  return previousDay.toISOString().split('T')[0];
};

const MarketMovers: React.FC = () => {
  const [activeMoverType, setActiveMoverType] = useState<MoverType>('gainers');
  const [data, setData] = useState<(MoverItem | PeSnapshotItem)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as MoverType | null;
    if (tab && moverTypeTabs.some(t => t.id === tab)) {
      setActiveMoverType(tab);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let response;
        console.log('Fetching data for mover type:', activeMoverType);
        if (activeMoverType === 'industry' || activeMoverType === 'sector') {
          const date = getPreviousWeekday();
          response = await StockService.getMarketPeSnapshot(activeMoverType, date);
          const rawData = response?.data || response;
          if (Array.isArray(rawData)) {
            const normalized = rawData
              .map(item => normalizePeSnapshotItem(item, activeMoverType))
              .filter((x): x is PeSnapshotItem => !!x);

            // Sort data alphabetically by name for consistent order
            normalized.sort((a, b) => a.name.localeCompare(b.name));

            setData(normalized);
          } else {
            setData([]);
          }
        } else {
          response = await StockService.getMarketMovers(activeMoverType);
          const rawData = response?.data || response;
          if (Array.isArray(rawData)) {
            const normalized = rawData.map(normalizeApiItem).filter((x): x is MoverItem => !!x);
            setData(normalized);
          } else {
            setData([]);
          }
        }
      } catch (err) {
        setError('Failed to fetch data. Please try again later.');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeMoverType]);

  const formatPrice = (p: number) => `$${p?.toFixed(2)}`;
  const formatChangeValue = (c: number) => {
    const sign = c > 0 ? '+' : '';
    return `${sign}${c.toFixed(2)}`;
  };
  const formatChangePercent = (cp: number) => {
    const sign = cp > 0 ? '+' : '';
    return `${sign}${cp.toFixed(2)}%`;
  };
  const colorFor = (n: number) => (n > 0 ? 'text-emerald-400' : n < 0 ? 'text-red-400' : 'text-theme-muted');

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin active-text" />
          <span className="ml-3 text-theme-muted">Loading...</span>
        </div>
      );
    }
    if (error) {
      return <div className="text-center py-10 text-red-400">{error}</div>;
    }
    if (data.length === 0) {
      return <div className="text-center py-10 text-theme-muted">No data available for the selected criteria.</div>;
    }

    if (activeMoverType === 'sector') {
      const sectorData = data as PeSnapshotItem[];
      return (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-theme-muted text-xs border-b border-theme-tertiary/40">
              <th className="py-2 pr-2 text-left font-medium">Sector</th>
              <th className="py-2 pr-2 text-right font-medium">P/E Ratio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-theme-tertiary/30">
            {sectorData.map((item) => (
              <tr key={item.name} className="hover:bg-theme-secondary/40">
                <td className="py-2 pr-2 font-semibold highlight-text whitespace-nowrap">{item.name}</td>
                <td className="py-2 pr-2 text-right font-semibold">{Math.round(item.pe)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (activeMoverType === 'industry') {
      const industryData = data as PeSnapshotItem[];
      return (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-theme-muted text-xs border-b border-theme-tertiary/40">
              <th className="py-2 pr-2 text-left font-medium">Industry</th>
              <th className="py-2 pr-2 text-right font-medium">P/E Ratio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-theme-tertiary/30">
            {industryData.map((item) => (
              <tr key={item.name} className="hover:bg-theme-secondary/40">
                <td className="py-2 pr-2 font-semibold highlight-text whitespace-nowrap">{item.name}</td>
                <td className="py-2 pr-2 text-right font-semibold">{Math.round(item.pe)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    console.log("data",data);
    const movers = data as MoverItem[];
    return (
      <table className="w-full text-sm">
        <thead>
          <tr className="text-theme-muted text-xs border-b border-theme-tertiary/40">
            <th className="py-2 pr-2 text-left font-medium">Symbol</th>
            <th className="py-2 pr-2 text-left font-medium">Name</th>
            <th className="py-2 pr-2 text-right font-medium">Price</th>
            <th className="py-2 pr-2 text-right font-medium">Change</th>
            <th className="py-2 pr-2 text-right font-medium">Change %</th>
            <th className="py-2 pr-2 text-left font-medium">Exchange</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-theme-tertiary/30">
          {movers?.map((m) => (
            <tr
              key={m.symbol}
              className="hover:bg-theme-secondary/40 cursor-pointer"
              onClick={() => navigate(`/${m.symbol}/summary`)}
            >
              <td className="py-2 pr-2 font-semibold highlight-text whitespace-nowrap">{m.symbol}</td>
              <td className="py-2 pr-2 truncate max-w-[220px]" title={m.name}>{m.name}</td>
              <td className="py-2 pr-2 text-right whitespace-nowrap">{formatPrice(m.price)}</td>
              <td className={`py-2 pr-2 text-right font-semibold ${colorFor(m.change)}`}>{formatChangeValue(m.change)}</td>
              <td className={`py-2 pr-2 text-right font-semibold ${colorFor(m.changePercent)}`}>{formatChangePercent(m.changePercent)}</td>
              <td className="py-2 pr-2 whitespace-nowrap">{m.exchange || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="bg-theme-secondary/30 rounded-xl border border-theme-tertiary/40 backdrop-blur-sm">
      <div className="px-5 pt-5">
        <h2 className="text-xl font-semibold text-theme-primary">Market Movers</h2>
        <div className="mt-4 border-b border-theme-tertiary/40">
          <nav className="-mb-px flex space-x-6">
            {moverTypeTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveMoverType(tab.id);
                  const params = new URLSearchParams(location.search);
                  params.set('tab', tab.id);
                  setData([]);
                  navigate(`/market-movers?${params.toString()}`);
                }}
                className={`whitespace-nowrap py-2 px-1 outline-none border-b-2 font-medium text-sm transition-colors ${activeMoverType === tab.id
                    ? 'active-border text-blue-400'
                    : 'border-transparent text-theme-muted hover:text-theme-primary'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
      <div className="p-5 pt-3 overflow-x-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default MarketMovers;
